import { displayErrorAndLog, logError } from './events/bk-error';
import constants from '../../shared/bk/modules/constants';

const fetchData = async (url) => {
    try {
        const response = await fetch(url);
        const data = await response.json();
        bkLoadBackendData.setStoredItem(url, data);

        return data;
    } catch {
        logError(new Error(`[FE - Error] bk-load-backend-data.js, failed to load data from: ${url}`));

        return {};
    }
};

/**
 * Object representing the backend data loader.
 * @namespace bkLoadBackendData
 */
export default class bkLoadBackendData {
    /**
     * Retrieves the account data asynchronously.
     * @async
     * @function getAccount
     * @memberof bkLoadBackendData
     * @param {number} [refreshAfterSeconds=30] - The time-to-live (TTL) value in seconds for the cached data.
     * @returns {Promise} A promise that resolves to the account data.
     */
    static async getAccount(refreshAfterSeconds = 30) {
        const url = `${constants.contextPath}app/config/account`;
        const stored = bkLoadBackendData.getStoredItem(url, refreshAfterSeconds);

        return stored || fetchData(url);
    }

    static async fetchAccountData(data) {
        try {
            const accountData = await bkLoadBackendData.getAccount();

            return accountData?.[data] ?? false;
        } catch (error) {
            displayErrorAndLog(error);

            return false;
        }
    }

    /**
     * Retrieves the user rights data asynchronously.
     * @async
     * @function user
     * @memberof bkLoadBackendData
     * @returns {Promise} A promise that resolves to the user rights data.
     */
    static async user() {
        const url = `${constants.contextPath}app/config/user`;
        const stored = bkLoadBackendData.getStoredItem(url);

        return stored || fetchData(url);
    }

    /**
     * Retrieves a stored item from session storage
     * @async
     * @function getStoredItem
     * @memberof bkLoadBackendData
     * @param {string} key - The key of the item to retrieve.
     * @param {number} [refreshAfterSeconds=30] - The time-to-live (TTL) value in seconds for the cached data.
     * @returns { Object | Array | string | number | boolean | null | undefined } the stored item or undefined
     */
    static getStoredItem(key, refreshAfterSeconds = 30) {
        const data = sessionStorage.getItem(key);
        const cachedTime = sessionStorage.getItem(key + '_time');

        if (refreshAfterSeconds && data && cachedTime) {
            const currentTime = new Date().getTime();
            if (currentTime - parseInt(cachedTime) < refreshAfterSeconds * 1000) {
                return JSON.parse(data);
            }
        }

        return undefined;
    }

    static setStoredItem(url, data) {
        sessionStorage.setItem(url, JSON.stringify(data));
        sessionStorage.setItem(url + '_time', new Date().getTime().toString());
    }
}
